import React, { useState } from 'react';
import { X, FileCheck, AlertCircle, Loader } from 'lucide-react';

const AnalysisResults = ({ results, onClose, inPanel = false }) => {
  // Predefined order for the agents
  const agentOrder = ['Citation', 'SFEE', 'EOF', 'Galenica', 'EFEX', 'Grammar'];
  
  // Initialize state variables at the top level of the component
  const initialActiveTab = results !== 'loading' && results.agents && results.agents.length > 0 
    ? results.agents
        .filter(agent => results.results[agent] !== null)
        .sort((a, b) => agentOrder.indexOf(a) - agentOrder.indexOf(b))[0] || ''
    : '';
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  
  // Handle loading state
  if (results === 'loading' && !inPanel) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-dark rounded-xl shadow-xl max-w-lg w-full mx-auto p-8 text-center animate-fade-in">
          <Loader size={48} className="mx-auto mb-4 text-primary animate-spin" />
          <h3 className="font-medium text-lg mb-2">Running Analysis</h3>
          <p className="text-gray-400">
            Processing document page with selected agents. This may take a few moments...
          </p>
        </div>
      </div>
    );
  }
  
  // If results is loading and we're in panel mode, we handle loading elsewhere
  if (results === 'loading' && inPanel) {
    return null;
  }
  
  // Get list of available agents that have results
  const agentsWithResults = results.agents
    .filter(agent => results.results[agent] !== null)
    .sort((a, b) => agentOrder.indexOf(a) - agentOrder.indexOf(b));
  
  // Check if there was an error
  const hasError = results.error || agentsWithResults.length === 0;
  
  // The main content of the analysis results
  const analysisContent = (
    <>
      {hasError ? (
        <div className="p-8 text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h3 className="font-medium text-lg mb-2">Analysis Error</h3>
          <p className="text-gray-400 mb-4">
            {results.errorMessage || "There was an error analyzing the document. Please try again."}
          </p>
          {!inPanel && (
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white font-medium"
            >
              Close
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="border-b border-dark-lighter">
            <div className="flex overflow-x-auto">
              {agentsWithResults.map((agent) => (
                <button
                  key={agent}
                  onClick={() => setActiveTab(agent)}
                  className={`px-4 py-3 flex items-center whitespace-nowrap ${
                    activeTab === agent
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-400 hover:text-white hover:bg-dark-lighter'
                  }`}
                >
                  {getAgentIcon(agent)}
                  <span className="ml-2">{`${agent} Agent`}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Content */}
          <div className={`p-6 ${inPanel ? 'max-h-full' : 'max-h-[60vh]'} overflow-y-auto`}>
            {!activeTab ? (
              // If no tab is selected yet, display a welcome message instead of error
              <div className="text-center p-4">
                <h3 className="font-medium text-lg mb-2">Analysis Complete</h3>
                <p className="text-gray-400 mb-4">
                  Please select an agent tab above to view detailed results.
                </p>
              </div>
            ) : results.results[activeTab] ? (
              <div>
                <div className="flex items-start mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mr-3">
                    {getAgentIcon(activeTab, 24)}
                  </div>
                  <div>
                    <h3 className="font-medium text-lg">{`${activeTab} Analysis`}</h3>
                    <p className="text-sm text-gray-400">
                      Completed on {new Date().toLocaleString()}
                    </p>
                  </div>
                </div>
                
                {/* Format the results based on agent type */}
                <div className="bg-dark-lighter p-4 rounded-lg">
                  {formatAgentResults(activeTab, results.results[activeTab])}
                </div>
                
                {/* Show the extracted text for context */}
                {results.extractedText && (
                  <div className="mt-4">
                    <h4 className="text-sm text-gray-400 uppercase font-medium mb-2">Analyzed Text</h4>
                    <div className="bg-dark-lighter p-4 rounded-lg">
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">
                        {results.extractedText}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-8">
                <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
                <h3 className="font-medium text-lg mb-2">No Results Available</h3>
                <p className="text-gray-400">
                  There are no analysis results available for this agent.
                </p>
              </div>
            )}
          </div>
          
          {/* Footer - Only shown in modal mode, not in panel mode */}
          {!inPanel && (
            <div className="border-t border-dark-lighter p-4 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white font-medium"
              >
                Close
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
  
  // If in panel mode, return just the content without the modal wrapper
  if (inPanel) {
    return analysisContent;
  }
  
  // Otherwise, wrap in a modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-dark rounded-xl shadow-xl max-w-4xl w-full mx-auto overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-dark-lighter p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold flex items-center">
            <FileCheck size={20} className="mr-2 text-primary" />
            Analysis Results
          </h2>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-dark-darker text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>
        
        {analysisContent}
      </div>
    </div>
  );
};

// Format the agent results based on agent type
const formatAgentResults = (agentId, results) => {
  // If results is null or empty, return a message
  if (!results) {
    return <p>No analysis results available.</p>;
  }

  // Handle different agent types
  switch (agentId) {
    case 'Citation':
      return formatCitationResults(results);
    case 'Grammar':
      return formatGrammarResults(results);
    case 'SFEE':
    case 'EOF':
    case 'Galenica':
    case 'EFEX':
      return formatComplianceResults(results, agentId);
    default:
      // Fallback to basic formatting
      return <pre className="whitespace-pre-wrap text-sm overflow-auto">{results}</pre>;
  }
};

// Format citation analysis results
const formatCitationResults = (results) => {
  // Extract all citations analyzed
  const citationsMatch = results.match(/All Citations Analyzed:(.*?)(?=\n\nIssues Flagged:|$)/s);
  const citationsAnalyzed = citationsMatch ? citationsMatch[1].trim() : "";

  // Extract issues flagged
  const issuesMatch = results.match(/Issues Flagged:(.*?)$/s);
  const issuesFlagged = issuesMatch ? issuesMatch[1].trim() : "";

  // Check if there are any issues (regardless of priority)
  const hasIssues = issuesFlagged.trim().length > 0;

  // Set status based on whether there are any issues
  const isAccurate = !hasIssues;
  const statusColor = isAccurate ? 'text-green-400' : 'text-red-400';

  // Generate binary assessment
  let customAssessment = isAccurate 
    ? 'All citations are accurate. No issues detected.' 
    : 'Some citations require revision. Issues have been identified.';

  // Format each citation with binary (accurate/inaccurate) status
  const formattedCitations = citationsAnalyzed.split(/CITATION #\d+/).filter(Boolean).map((citation, index) => {
    // Check if the citation explicitly mentions accuracy status
    const accuracyMatch = citation.match(/Accuracy:\s*(Accurate|Inaccurate)/i);
    const explicitAccuracy = accuracyMatch ? accuracyMatch[1].toLowerCase() === 'accurate' : null;
    
    // Also check for priority tags as a fallback
    const hasPriorityTag = /\[(HIGH|MEDIUM|LOW)\]/.test(citation);
    
    // Determine accuracy status - prioritize explicit accuracy if available
    const isAccurate = explicitAccuracy !== null ? explicitAccuracy : !hasPriorityTag;
    
    // Set color based on accurate/inaccurate status
    const statusColor = isAccurate 
      ? 'bg-green-900/20 border-green-700' 
      : 'bg-red-900/20 border-red-700';
    
    // Clean up the citation text by removing the priority tag
    const cleanCitation = citation.replace(/\[(HIGH|MEDIUM|LOW)\]/, '').trim();
    
    return (
      <div key={`citation-${index}`} className={`mb-4 p-3 rounded-lg border ${statusColor}`}>
        <div className="flex items-center mb-2">
          <span className="font-medium mr-2">CITATION #{index + 1}</span>
          <span className={`px-3 py-1 rounded-full text-sm ${
            isAccurate ? 'bg-green-700 text-white' : 'bg-red-700 text-white'
          }`}>
            {isAccurate ? 'Accurate' : 'Inaccurate'}
          </span>
        </div>
        <div className="pl-2 border-l-2 border-gray-600">
          {cleanCitation.split('\n').filter(Boolean).map((line, i) => (
            <p key={i} className="mb-1 text-sm">{line.trim()}</p>
          ))}
        </div>
      </div>
    );
  });

  // Format each issue - without priority levels
  const formattedIssues = issuesFlagged.split(/ISSUE #\d+/).filter(Boolean).map((issue, index) => {
    // Extract type, description, and recommendation
    const typeMatch = issue.match(/Type:(.*?)(?=\n|$)/);
    const descriptionMatch = issue.match(/Description:(.*?)(?=\nCitation:|$)/s);
    const recommendationMatch = issue.match(/Recommendation:(.*?)(?=\n\n|$)/s);
    
    const issueType = typeMatch ? typeMatch[1].trim() : "";
    // Clean up the description by removing the priority tag
    let description = descriptionMatch ? descriptionMatch[1].trim() : "";
    description = description.replace(/\[(HIGH|MEDIUM|LOW)\]/, '').trim();
    
    const recommendation = recommendationMatch ? recommendationMatch[1].trim() : "";
    
    return (
      <div key={`issue-${index}`} className="mb-4 p-3 rounded-lg border bg-red-900/20 border-red-700">
        <div className="flex items-center mb-2">
          <span className="font-medium mr-2">ISSUE #{index + 1}</span>
          <span className="px-3 py-1 rounded-full text-sm bg-red-700 text-white">
            Citation Issue
          </span>
        </div>
        {issueType && (
          <div className="mb-2">
            <span className="text-gray-400 text-sm">Type:</span>
            <span className="ml-2 text-sm">{issueType}</span>
          </div>
        )}
        {description && (
          <div className="mb-2">
            <span className="text-gray-400 text-sm">Description:</span>
            <p className="mt-1 text-sm pl-2 border-l-2 border-gray-600">{description}</p>
          </div>
        )}
        {recommendation && (
          <div className="mb-2">
            <span className="text-gray-400 text-sm">Recommendation:</span>
            <p className="mt-1 text-sm pl-2 border-l-2 border-primary/50">{recommendation}</p>
          </div>
        )}
      </div>
    );
  });

  return (
    <div>
      {/* Overall Assessment */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-2">Overall Assessment</h3>
        <div className={`p-4 rounded-lg ${
          isAccurate ? 'bg-green-900/20 border border-green-700' : 'bg-red-900/20 border border-red-700'
        }`}>
          <span className={statusColor}>{customAssessment}</span>
        </div>
      </div>
      
      {/* Citations Analyzed */}
      {formattedCitations.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Citations Analyzed</h3>
          <div className="space-y-3">
            {formattedCitations}
          </div>
        </div>
      )}
      
      {/* Issues Flagged */}
      {formattedIssues.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Issues Flagged</h3>
          <div className="space-y-3">
            {formattedIssues}
          </div>
        </div>
      )}
    </div>
  );
};

// Format grammar results
const formatGrammarResults = (results) => {
  // Split into individual issues
  const issues = results.split(/\n\n/).filter(Boolean);
  
  if (issues.length === 0) {
    return (
      <div className="p-4 bg-green-900/20 border border-green-700 rounded-lg text-center">
        <span className="text-green-400">No grammar issues detected.</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {issues.map((issue, index) => {
        // Extract parts of the issue
        const exactTextMatch = issue.match(/exact_text:(.*?)(?=\nerror:|$)/);
        const errorMatch = issue.match(/error:(.*?)(?=\nrecommendation:|$)/);
        const recommendationMatch = issue.match(/recommendation:(.*?)(?=\n|$)/);
        
        const exactText = exactTextMatch ? exactTextMatch[1].trim() : "";
        const error = errorMatch ? errorMatch[1].trim() : "";
        const recommendation = recommendationMatch ? recommendationMatch[1].trim() : "";
        
        return (
          <div key={`grammar-${index}`} className="p-3 bg-red-900/20 border border-red-700 rounded-lg">
            {exactText && (
              <div className="mb-2">
                <span className="text-gray-400 text-sm">Problematic Text:</span>
                <div className="mt-1 p-2 bg-dark-darker rounded text-sm font-mono">{exactText}</div>
              </div>
            )}
            {error && (
              <div className="mb-2">
                <span className="text-gray-400 text-sm">Error:</span>
                <p className="mt-1 text-sm text-red-400">{error}</p>
              </div>
            )}
            {recommendation && (
              <div>
                <span className="text-gray-400 text-sm">Recommendation:</span>
                <p className="mt-1 text-sm text-green-400">{recommendation}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Format compliance results (SFEE, EOF, Menarini)
const formatComplianceResults = (results, agentType) => {
  // Extract the report title for styling
  const titleMatch = results.match(/(.*?COMPLIANCE ANALYSIS REPORT)|(.*?COMPLIANCE REPORT)/);
  const title = titleMatch ? titleMatch[0].trim() : `${agentType} Analysis`;
  
  // Split into individual findings
  const findingMatches = [...results.matchAll(/Finding \d+:(.*?)(?=Finding \d+:|---\n|$)/gs)];
  
  if (findingMatches.length === 0) {
    return (
      <div>
        <h3 className="text-lg font-medium mb-4">{title}</h3>
        <div className="p-4 bg-dark-lighter rounded-lg">
          <p className="text-gray-400">No specific findings reported.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h3 className="text-lg font-medium mb-4">{title}</h3>
      
      <div className="space-y-4">
        {findingMatches.map((match, index) => {
          const findingContent = match[1].trim();
          
          // Extract components
          const textMatch = findingContent.match(/Text:(.*?)(?=\nCompliant:|$)/s);
          const compliantMatch = findingContent.match(/Compliant:(.*?)(?=\n|$)/);
          
          // Check for both Assessment (compliant) and Issue (non-compliant) patterns
          const assessmentMatch = findingContent.match(/Assessment:(.*?)(?=\n\n|$)/s);
          const issueMatch = findingContent.match(/Issue:(.*?)(?=\nRecommendation:|$)/s);
          const recommendationMatch = findingContent.match(/Recommendation:(.*?)(?=\n\n|$)/s);
          
          const text = textMatch ? textMatch[1].trim() : "";
          const compliant = compliantMatch ? compliantMatch[1].trim() : "";
          
          // Use either assessment or issue depending on compliance status
          const assessment = assessmentMatch ? assessmentMatch[1].trim() : "";
          const issue = issueMatch ? issueMatch[1].trim() : "";
          const recommendation = recommendationMatch ? recommendationMatch[1].trim() : "";
          
          // Determine compliance status
          const isCompliant = compliant.includes("Yes") || compliant.includes("✅");
          
          return (
            <div 
              key={`finding-${index}`} 
              className={`p-4 rounded-lg border ${
                isCompliant ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'
              }`}
            >
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium">Finding {index + 1}</span>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  isCompliant ? 'bg-green-700 text-white' : 'bg-red-700 text-white'
                }`}>
                  {isCompliant ? 'Compliant' : 'Non-Compliant'}
                </span>
              </div>
              
              {text && (
                <div className="mb-3">
                  <span className="text-gray-400 text-sm">Analyzed Text:</span>
                  <div className="mt-1 p-2 bg-dark-darker rounded text-sm">
                    {text}
                  </div>
                </div>
              )}
              
              {isCompliant && assessment && (
                <div>
                  <span className="text-gray-400 text-sm">Assessment:</span>
                  <div className="mt-1 pl-2 border-l-2 border-gray-600 text-sm">
                    {assessment}
                  </div>
                </div>
              )}
              
              {!isCompliant && (
                <>
                  {issue && (
                    <div className="mb-3">
                      <span className="text-gray-400 text-sm">Issue:</span>
                      <div className="mt-1 pl-2 border-l-2 border-red-700/50 text-sm">
                        {issue}
                      </div>
                    </div>
                  )}
                  {recommendation && (
                    <div>
                      <span className="text-gray-400 text-sm">Recommendation:</span>
                      <div className="mt-1 pl-2 border-l-2 border-primary/50 text-sm">
                        {recommendation}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper function to get agent icon
const getAgentIcon = (agentId, size = 16) => {
  const icons = {
    SFEE: <span style={{ fontSize: size === 16 ? '1rem' : '1.5rem' }}>🔍</span>,
    EOF: <span style={{ fontSize: size === 16 ? '1rem' : '1.5rem' }}>📝</span>,
    Galenica: <span style={{ fontSize: size === 16 ? '1rem' : '1.5rem' }}>⚕️</span>,
    EFEX: <span style={{ fontSize: size === 16 ? '1rem' : '1.5rem' }}>📊</span>,
    Grammar: <span style={{ fontSize: size === 16 ? '1rem' : '1.5rem' }}>📚</span>,
    Citation: <span style={{ fontSize: size === 16 ? '1rem' : '1.5rem' }}>📋</span>,
  };
  
  return icons[agentId] || null;
};

export default AnalysisResults;